import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { NotebookPen, Trash2, Plus } from "lucide-react";

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", category: "General" });

  const load = () => api.get("/notes").then((r) => setNotes(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/notes/${editing}`, form);
      else await api.post("/notes", form);
      toast.success(editing ? "Nota actualizada" : "Nota creada");
      setForm({ title: "", content: "", category: "General" }); setEditing(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => { await api.delete(`/notes/${id}`); load(); };

  const filtered = notes.filter((n) => (n.title + n.content + n.category).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fade-up" data-testid="notes-page">
      <h1 className="text-3xl font-semibold tracking-tight mb-1">Libro de Notas</h1>
      <p className="text-neutral-500 mb-8">Tu espacio privado. Nadie más puede ver estas notas.</p>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 h-fit">
          <h2 className="font-semibold mb-4 flex items-center gap-2"><Plus size={16} /> {editing ? "Editar nota" : "Nueva nota"}</h2>
          <div className="space-y-3">
            <Input data-testid="note-title-input" placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <select data-testid="note-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full h-10 rounded-md border border-neutral-300 px-3 text-sm">
              {["General", "Señales", "Normativa", "Maniobras", "Dudas", "Examen"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <Textarea data-testid="note-content-input" placeholder="Escribe tu nota…" rows={5} value={form.content}
                      onChange={(e) => setForm({ ...form, content: e.target.value })} />
            <div className="flex gap-2">
              <Button data-testid="note-save-btn" onClick={save} disabled={!form.title || !form.content}
                      className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
                {editing ? "Guardar cambios" : "Crear nota"}
              </Button>
              {editing && <Button variant="ghost" onClick={() => { setEditing(null); setForm({ title: "", content: "", category: "General" }); }}>Cancelar</Button>}
            </div>
          </div>
        </div>

        <div>
          <Input data-testid="note-search-input" placeholder="Buscar notas…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-4" />
          <div className="space-y-3">
            {filtered.length === 0 && <p className="text-sm text-neutral-500">No tienes notas todavía.</p>}
            {filtered.map((n) => (
              <div key={n.id} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-5" data-testid="note-card">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-orange-100 text-orange-700 rounded-full px-2.5 py-0.5">{n.category}</span>
                    <h3 className="font-semibold mt-2">{n.title}</h3>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid="note-edit-btn" onClick={() => { setEditing(n.id); setForm({ title: n.title, content: n.content, category: n.category }); }}
                            className="p-2 text-neutral-400 hover:text-orange-600"><NotebookPen size={15} /></button>
                    <button data-testid="note-delete-btn" onClick={() => del(n.id)} className="p-2 text-neutral-400 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                </div>
                <p className="text-sm text-neutral-600 mt-1 whitespace-pre-wrap">{n.content}</p>
                <p className="text-xs text-neutral-400 mt-2">{fmtDate(n.date)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
